import * as React from 'react';
import {
  FocusZoneDirection,
  IconButton,
  IIconProps,
  ContextualMenu,
  IContextualMenuItem,
  ContextualMenuItemType,
  mergeStyles,
  DirectionalHint,
  TooltipDelay,
  TooltipHost,
  ITooltipHostStyles,
  ITheme,
  styled,
  IStyleFunctionOrObject,
  IContextualMenuSubComponentStyles,
  IStyle,
  concatStyleSetsWithProps,
  setFocusVisibility
} from '@fluentui/react';
import { CommandHandler, CommandProps } from '@fluidx/office-fluid-types';
import {
  sendUserActionEvent,
  isMobile,
  getUIDirection,
  resolveLocale,
  useStringTable,
  convertCommandPropsDoubleArrayToSingleWithTree
} from '@fluidx/utilities';
import { useComponentsIcon } from '@fluidx/icons';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import {
  topBarSectionStyles,
  iconButtonStyles,
  getCalloutStyle,
  topBarDividerClassName,
  getContextualMenuStyles
} from './CommandingSurface.styles';
import { getStringTable } from '../../utilities/localizationUtilities';

export interface CommandingSurfaceProps {
  target: HTMLElement;
  children?: React.ReactNode;
  topBarCommands?: CommandProps[][];
  commandGroups?: CommandProps[][];
  onDismiss?: (ev?: any) => void;
  triggerByKeyboardEvent?: boolean;
  miniMode?: boolean;
  disableInitialFocus?: boolean;
  logger?: ITelemetryBaseLogger;
  cssProperties?: () => { [propertyName: string]: string } | undefined;
  locale: string | undefined;
  styles?: IStyleFunctionOrObject<CommandingSurfaceStyleProps, CommandingSurfaceStyles>;
  theme?: ITheme;
}

export interface CommandingSurfaceTreeProps {
  target: HTMLElement;
  children?: React.ReactNode;
  topBarCommands?: CommandProps[];
  commandGroups?: CommandProps[];
  onDismiss?: (ev?: any) => void;
  triggerByKeyboardEvent?: boolean;
  miniMode?: boolean;
  disableInitialFocus?: boolean;
  logger?: ITelemetryBaseLogger;
  cssProperties?: () => { [propertyName: string]: string } | undefined;
  locale: string | undefined;
  styles?: IStyleFunctionOrObject<CommandingSurfaceStyleProps, CommandingSurfaceStyles>;
  theme?: ITheme;
}

export interface CommandingSurfaceItemProps {
  type: 'IconButton' | 'Item';

  id: string;

  /**
   * label of the command
   */
  text: string;

  /**
   * shortcut hint to trigger the command
   */
  shortcut?: string;

  /**
   * icon to be displayed in the button
   */
  iconProps?: IIconProps;

  /**
   * callback of the command button
   */
  onClick: CommandHandler;

  /**
   * Command button toggle state
   */
  disabled?: boolean;

  /**
   * description of the command button
   */
  secondaryText?: string;

  /**
   * whether the command button can be toggled
   */
  toggle?: boolean;

  /**
   * Command button toggle state
   */
  checked?: boolean;
}

export interface CommandingSurfaceStyles {
  root?: IStyle;
  subComponentStyles?: IContextualMenuSubComponentStyles;
}

export interface CommandingSurfaceStyleProps extends Pick<CommandingSurfaceProps, 'theme'> {}

export const CommandingSurface = React.memo(
  styled(function CommandingSurface(props: CommandingSurfaceProps | CommandingSurfaceTreeProps) {
    const {
      topBarCommands: tempTopBarCommands,
      commandGroups: tempCommandGroups,
      target,
      onDismiss,
      triggerByKeyboardEvent,
      miniMode,
      disableInitialFocus,
      logger,
      cssProperties,
      locale,
      styles,
      theme
    } = props;

    let topBarCommands: CommandProps[] | undefined;
    let commandGroups: CommandProps[] | undefined;
    if (!tempTopBarCommands) {
      topBarCommands = undefined;
    } else if (tempTopBarCommands[0] && !('displayName' in tempTopBarCommands[0])) {
      topBarCommands = convertCommandPropsDoubleArrayToSingleWithTree(tempTopBarCommands as CommandProps[][]);
    } else {
      topBarCommands = tempTopBarCommands as CommandProps[];
    }
    if (!tempCommandGroups) {
      commandGroups = undefined;
    } else if (tempCommandGroups[0] && !('displayName' in tempCommandGroups[0])) {
      commandGroups = convertCommandPropsDoubleArrayToSingleWithTree(tempCommandGroups as CommandProps[][]);
    } else {
      commandGroups = tempCommandGroups as CommandProps[];
    }

    const [miniModeState, setMiniModeState] = React.useState<boolean | undefined>(miniMode);

    const stringTable = useStringTable(getStringTable, locale);
    const uiDirection = locale ? getUIDirection(resolveLocale(locale)) : 'ltr';
    if (theme) {
      theme.rtl = uiDirection === 'rtl';
    }

    const windowBlurListener = (event: FocusEvent) => {
      if (target && !event.relatedTarget && onDismiss) {
        onDismiss();
        window.removeEventListener('blur', windowBlurListener);
      }
    };

    window.addEventListener('blur', windowBlurListener);

    const toggleMenuHandler = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      setMiniModeState(!miniModeState);
    };

    const toggleMenuItem: IContextualMenuItem = {
      key: 'toggleMenu',
      id: 'toggleMenu',
      text: stringTable?.commandingSurfaceToggleBtnText,
      type: ContextualMenuItemType.Normal,
      checked: !miniModeState,
      iconProps: { iconName: useComponentsIcon('FFXCMore') },
      onClick: getOnClickHandler('ToggleMenu', toggleMenuHandler, logger, miniModeState)
    };

    const cssPropertiesClassName = cssProperties ? mergeStyles(cssProperties()) : undefined;
    const resolvedContextualMenuStyles = concatStyleSetsWithProps(props, styles);

    const commandingSurfaceItems = [
      ...getTopBarSection(topBarCommands, miniModeState, logger, onDismiss, toggleMenuItem),
      ...getCommandGroupsSections(
        topBarCommands,
        commandGroups,
        miniModeState,
        logger,
        onDismiss,
        cssPropertiesClassName,
        resolvedContextualMenuStyles,
        locale
      )
    ];

    const directionalHint = uiDirection === 'rtl' ? DirectionalHint.bottomRightEdge : DirectionalHint.bottomLeftEdge;

    if (triggerByKeyboardEvent && !disableInitialFocus) {
      setFocusVisibility(true);
    }

    return (
      <ContextualMenu
        items={commandingSurfaceItems}
        target={target}
        styles={resolvedContextualMenuStyles}
        theme={theme}
        isBeakVisible={false}
        focusZoneProps={{ direction: FocusZoneDirection.bidirectional }}
        shouldFocusOnContainer={!triggerByKeyboardEvent}
        shouldFocusOnMount={!disableInitialFocus}
        delayUpdateFocusOnHover={true}
        calloutProps={{
          preventDismissOnResize: isMobile(),
          className: getCalloutStyle(cssPropertiesClassName),
          onContextMenu: handleContextMenu,
          onKeyDown: handleKeyDown,
          onPointerDown: handlePointerDown
        }}
        directionalHint={directionalHint}
        onDismiss={onDismiss}
        onItemClick={onDismiss}
      />
    );
  }, getContextualMenuStyles)
);

function handleContextMenu(mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) {
  // Disable right-click from opening browser context menu
  mouseEvent.preventDefault();
  mouseEvent.stopPropagation();
}

function handleKeyDown(keyEvent: React.KeyboardEvent<HTMLDivElement>) {
  // Disable Ctrl + A from selecting all when the menu has focus
  if (keyEvent.ctrlKey && keyEvent.key === 'a') {
    keyEvent.preventDefault();
    keyEvent.stopPropagation();
  }
}

function handlePointerDown(pointerEvent: React.PointerEvent<HTMLDivElement>) {
  pointerEvent.preventDefault();
}

const defaultIsInlineSubmenu = true;

function getTopBarSection(
  topBarCommands: CommandProps[] | undefined,
  miniModeState: boolean | undefined,
  logger: ITelemetryBaseLogger | undefined,
  onDismiss: ((ev?: any) => void) | undefined,
  toggleMenuItem: IContextualMenuItem
): IContextualMenuItem[] {
  if (!topBarCommands) {
    return [];
  }

  const commandArray: IContextualMenuItem[] = [];

  const topBarSectionItem: IContextualMenuItem = {
    key: 'topbar_section',
    onClick: onDismiss,
    items: [],
    onRender: renderTopBarCommandsSection
  };

  topBarCommands &&
    topBarCommands.length > 0 &&
    topBarCommands.map((commandGroup) => {
      // For each group of commands
      if (!commandGroup.subMenuCommands) {
        const item: IContextualMenuItem = {
          key: commandGroup.id + '0',
          id: commandGroup.id,
          type: ContextualMenuItemType.Normal,
          text: commandGroup.displayName,
          iconProps: commandGroup.icon ? { iconName: commandGroup.icon } : undefined,
          onClick: commandGroup.execute
            ? getOnClickHandler(commandGroup.id, commandGroup.execute, logger, miniModeState)
            : undefined,
          shortcut: commandGroup.shortcut,
          secondaryText: commandGroup.secondaryText,
          disabled: commandGroup.disabled,
          checked: commandGroup.checked
        };
        commandArray.push(item);
      } else {
        commandGroup.subMenuCommands.map((command, index) => {
          // For each command in a group, create item & add to array
          const item: IContextualMenuItem = {
            key: command.id + index.toString(),
            id: command.id,
            type: ContextualMenuItemType.Normal,
            text: command.displayName,
            iconProps: command.icon ? { iconName: command.icon } : undefined,
            onClick: command.execute
              ? getOnClickHandler(command.id, command.execute, logger, miniModeState)
              : undefined,
            shortcut: command.shortcut,
            secondaryText: command.secondaryText,
            disabled: command.disabled,
            checked: command.checked
          };

          commandArray.push(item);
        });
      }
    }) &&
    commandArray.push(toggleMenuItem);

  if (topBarSectionItem.items && commandArray.length > 0) {
    topBarSectionItem.items = commandArray;
    topBarSectionItem.customOnRenderListLength = commandArray.length;
  }

  return [topBarSectionItem];
}

function getCommandGroupsSections(
  topBarCommands: CommandProps[] | undefined,
  commandGroups: CommandProps[] | undefined,
  miniModeState: boolean | undefined,
  logger: ITelemetryBaseLogger | undefined,
  onDismiss: ((ev?: any) => void) | undefined,
  cssPropertiesClassName: string | undefined,
  contextualMenuStyles: CommandingSurfaceStyles | undefined,
  locale: string | undefined
): IContextualMenuItem[] {
  if (miniModeState) {
    return [];
  }

  const commandSections: IContextualMenuItem[] = [];

  // Add top bar divider if necessary
  if (topBarCommands && topBarCommands.length > 0) {
    const topBarDivider: IContextualMenuItem = {
      key: 'divider',
      itemType: ContextualMenuItemType.Divider,
      className: topBarDividerClassName
    };
    commandSections.push(topBarDivider);
  }

  commandGroups &&
    commandGroups.length > 0 &&
    commandGroups.map((command, index) => {
      const subMenuItems = getSubMenuItems(command, onDismiss, cssPropertiesClassName, contextualMenuStyles, locale);

      const isSectionWithDefault = command.isInlineSubMenu ?? defaultIsInlineSubmenu;
      const itemType =
        subMenuItems.length > 0 && isSectionWithDefault // If there are submenu items and it is a section, the type is Section
          ? ContextualMenuItemType.Section
          : ContextualMenuItemType.Normal;

      const subMenuProps =
        subMenuItems.length > 0
          ? isSectionWithDefault
            ? {
                items: subMenuItems,
                bottomDivider: index < commandGroups.length - 1,
                topDivider:
                  commandSections.length > 0 &&
                  commandSections[commandSections.length - 1].itemType !== ContextualMenuItemType.Divider &&
                  commandSections[commandSections.length - 1].itemType !== ContextualMenuItemType.Section
              }
            : {
                items: subMenuItems,
                styles: contextualMenuStyles,
                calloutProps: {
                  preventDismissOnResize: isMobile(),
                  className: getCalloutStyle(cssPropertiesClassName),
                  onContextMenu: handleContextMenu,
                  onKeyDown: handleKeyDown,
                  onPointerDown: handlePointerDown
                }
              }
          : undefined;

      // For each command in a group, create item & add to the section's sectionProps.items array
      const item: IContextualMenuItem = {
        key: command.id + index.toString(),
        id: command.id,
        itemType,
        text: command.displayName,
        iconProps: command.icon ? { iconName: command.icon } : undefined,
        onClick: command.execute ? getOnClickHandler(command.id, command.execute, logger, miniModeState) : undefined,
        secondaryText: command.shortcut ? command.shortcut : command.secondaryText,
        disabled: command.disabled,
        checked: command.checked,
        sectionProps: isSectionWithDefault ? subMenuProps : undefined,
        subMenuProps: isSectionWithDefault ? undefined : subMenuProps,
        submenuIconProps:
          !isSectionWithDefault && subMenuProps
            ? { iconName: useComponentsIcon('FFXCChevronRight', resolveLocale(locale)) }
            : undefined,
        onDismiss
      };

      commandSections.push(item);
    });
  return commandSections;
}

export function getOnClickHandler(
  commandName: string,
  commandHandler: CommandHandler,
  logger: ITelemetryBaseLogger | undefined,
  miniMode: boolean | undefined
): CommandHandler {
  return (ev?: any) => {
    commandHandler(ev);

    logger &&
      sendUserActionEvent(logger, {
        eventName: `Click${commandName}`,
        commandSurface: `CommandingSurface.${miniMode ? 'Collapsed' : 'Expanded'}`
      });
  };
}

function getSubMenuItems(
  command: CommandProps,
  onDismiss: ((ev?: any) => void) | undefined,
  cssPropertiesClassName: string | undefined,
  contextualMenuStyles: CommandingSurfaceStyles | undefined,
  locale: string | undefined
): IContextualMenuItem[] {
  const subMenuItems: IContextualMenuItem[] = [];

  command.subMenuCommands &&
    command.subMenuCommands.length > 0 &&
    command.subMenuCommands.forEach((subCommand, index) => {
      // Recursively get all subMenu levels
      const nextSubMenuLevelItems = getSubMenuItems(
        subCommand,
        onDismiss,
        cssPropertiesClassName,
        contextualMenuStyles,
        locale
      );

      const subMenuItem: IContextualMenuItem = {
        key: subCommand.id + index.toString(),
        id: subCommand.id,
        itemType: ContextualMenuItemType.Normal,
        text: subCommand.displayName,
        iconProps: subCommand.icon ? { iconName: subCommand.icon } : undefined,
        onClick: subCommand.execute,
        secondaryText: subCommand.shortcut ? subCommand.shortcut : subCommand.secondaryText,
        disabled: subCommand.disabled,
        checked: subCommand.checked,
        onDismiss
      };

      if (nextSubMenuLevelItems.length > 0) {
        const isSectionWithDefault = subCommand.isInlineSubMenu ?? defaultIsInlineSubmenu;
        const subMenuProps = isSectionWithDefault
          ? {
              items: nextSubMenuLevelItems,
              bottomDivider: index < command.subMenuCommands!.length - 1
            }
          : {
              items: nextSubMenuLevelItems,
              styles: contextualMenuStyles,
              calloutProps: {
                preventDismissOnResize: isMobile(),
                className: getCalloutStyle(cssPropertiesClassName),
                onContextMenu: handleContextMenu,
                onKeyDown: handleKeyDown,
                onPointerDown: handlePointerDown
              }
            };
        if (isSectionWithDefault) {
          subMenuItem.itemType = ContextualMenuItemType.Section;
          subMenuItem.sectionProps = subMenuProps;
        } else {
          subMenuItem.subMenuProps = subMenuProps;
          subMenuItem.submenuIconProps = { iconName: useComponentsIcon('FFXCChevronRight', resolveLocale(locale)) };
        }
      }

      subMenuItems.push(subMenuItem);
    });

  return subMenuItems;
}

function renderTopBarCommandsSection(item: IContextualMenuItem): JSX.Element {
  const tooltipHostStyles: Partial<ITooltipHostStyles> = { root: { display: 'inline-block' } };

  return (
    <ul role="menu" className={topBarSectionStyles} onClick={item.onClick}>
      {item.items.map((command: any) => {
        return (
          <li key={command.key} role="presentation" style={{ listStyle: 'none' }}>
            <TooltipHost content={command.text} styles={tooltipHostStyles} delay={TooltipDelay.long}>
              <IconButton
                role="menuitemcheckbox"
                data-automation-id={command.id}
                ariaLabel={command.text}
                checked={command.checked}
                disabled={command.disabled}
                iconProps={command.iconProps}
                onClick={command.onClick}
                styles={iconButtonStyles}
                toggle={command.toggle}
                allowDisabledFocus={true}
              />
            </TooltipHost>
          </li>
        );
      })}
    </ul>
  );
}
